
import {defineComponent, PropType} from "vue";
import {WysiwygContentPageRestDto} from "@/api/pharma-cms-content-page/models";
import {ContentBlockTypesEnum} from "@/helpers/functions/enum";
import CbTextWithImage from "@/components/layouts/content-pages/wysiwyg/structures/CbTextWithImage.vue";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";
import {FormMixin} from "@/components/base/FormMixin";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import {CbStructureApi} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureApi";
import {ContentPageRestService} from "@/services/rest/cms-content-page/ContentPageRestService";
import {ContentPageRestDto} from "@/api/pharma-cms-content-page/models";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";

export default defineComponent({
  name: "ContentPagePageAbstract",
  emits: ["saved"],
  mixins: [FormMixin],
  components: {GenericFormWrapper, BaseOffcanvasButtonClose, CbTextWithImage},
  props: {
    /**
     * The page whose overview content block is edited here. Handed down instead of read from a store,
     * so this offcanvas and the canvas behind it can never disagree about which page they show.
     */
    contentPage: {
      type: Object as PropType<WysiwygContentPageRestDto>,
      required: true,
    },
  },
  data() {
    return {
      contentBlockOverviewType: ContentBlockTypesEnum.OVERVIEW_INFO as string,
    };
  },
  mounted() {
    this.initialize();
  },
  methods: {
    async doInitialize(): Promise<void> {
      // Nothing to load up front; the embedded content block loads itself from its cb-id.
    },
    async doReload(): Promise<void> {
      // Nothing to fetch here. reloadContent() takes the form out of its ready state, which unmounts
      // the embedded content block; on remount it loads itself from its cb-id watcher.
    },
    /**
     * Saves the overview content block, then points the page at it.
     *
     * One awaited chain: if the content block fails to save, the page is not updated and the failure
     * is reported as a failure.
     */
    async doSubmit(): Promise<void> {
      const newCbId = await this.cbStructure()?.submit() ?? null;

      if (!this.contentPage.cp) {
        return;
      }

      // Built as a copy rather than written onto the prop: the page owns its own state and picks the
      // new overview_cb_id back up through the reload triggered by the 'saved' event below.
      const contentPage: ContentPageRestDto = {
        ...this.contentPage.cp,
        overview_cb_id: newCbId ?? this.contentPage.cp.overview_cb_id,
      };

      await ContentPageRestService.getInstance()
        .updateContentPage(contentPage, this.contentPage.id);
    },
    async afterSave(): Promise<void> {
      await handleSavedSuccessfully();
      await this.reloadContent();

      this.$emit('saved');
    },
    /**
     * Called by the page when this offcanvas is opened, so the block shows what is stored right now.
     * Was the 'openPageAbstract' bus channel.
     */
    async reload(): Promise<void> {
      await this.reloadContent();
    },
    cbStructure(): CbStructureApi | undefined {
      return this.$refs.cbStructure as CbStructureApi | undefined;
    },
  },
});
